import { produce } from 'immer';
import { atom, selector } from 'recoil';

import { vehiclesState } from '@/states/used-cars/vehicles.state';
import { CarFilter } from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const doorsFilterState = atom<CarFilter[]>({
  key: 'doorsFilterState',
  default: [],
});

export const doorsFilterSearchOptionsSelector = selector({
  key: 'doorsFilterSearchOptionsSelector',
  get: ({ get }) => {
    const doorsTypes = get(doorsFilterState);
    const doorsTypeSearchOptions: CarFilter[] = doorsTypes.filter(
      (option) => !option.isSelected,
    );

    return { doorsTypeSearchOptions };
  },
});

export const toggleDoors = selector<number | null>({
  key: 'toggleDoors',
  get: () => null,
  set: ({ set, get }, doorsTypeId) => {
    const doorsTypes = get(doorsFilterState);

    const updatedDoorsType = produce(doorsTypes, (doorsTypesDraft) => {
      if (doorsTypeId) {
        const doorsType = doorsTypesDraft.find(
          (doorsType) => doorsType.id === doorsTypeId,
        );

        doorsType!.isSelected = !doorsType?.isSelected;
        return;
      }
    });

    set(doorsFilterState, updatedDoorsType);
  },
});

export const selectedDoorsFilterSelector = selector<CarFilter[]>({
  key: 'selectedDoorsFilterSelector',
  get: ({ get }) => {
    const doorsTypes = get(doorsFilterState);

    return doorsTypes.filter((doorsType) => doorsType.isSelected);
  },
});

export const visibleDoorsTypesSelector = selector<CarFilter[]>({
  key: 'visibleDoorsTypesSelector',
  get: ({ get }) => {
    const doorsTypes = get(doorsFilterState);
    const vehicles = get(vehiclesState);

    const bodyDoorsId = [
      ...new Set(vehicles.map((vehicle) => vehicle.bodyDoorsId)),
    ];

    return doorsTypes.map((doorsType) => ({
      ...doorsType,
      isDisabled: !bodyDoorsId.includes(doorsType.id),
    }));
  },
});

export const hasSelectedDoorsFilterSelector = selector<boolean>({
  key: 'hasSelectedDoorsFilterSelector',
  get: ({ get }) => {
    return Boolean(get(selectedDoorsFilterSelector).length);
  },
});

export const resetSelectedDoorsFilterSelector = selector<number | null>({
  key: 'resetSelectedDoorsFilterSelector',
  get: () => null,
  set: ({ set, get }, filterId) => {
    const doorsTypes = get(doorsFilterState);

    const updatedDoorsType = produce(doorsTypes, (doorsTypesDraft) => {
      if (filterId) {
        const doorsType = doorsTypesDraft.find(
          (doorsType) => doorsType.id === filterId,
        );

        doorsType!.isSelected = false;
        return;
      }

      doorsTypesDraft.forEach((doorsType) => {
        if (filterId && doorsType.id === filterId) {
          doorsType.isSelected = false;
          return;
        }
        doorsType.isSelected = false;
      });
    });

    set(doorsFilterState, updatedDoorsType);
  },
});

export const selectDoors = selector({
  key: 'selectDoors',
  get: () => [],
  set: ({ set, get }, doorsTypeIds) => {
    const doorsTypes = get(doorsFilterState);

    const updatedDoorsType = produce(doorsTypes, (doorsTypesDraft) => {
      if (guardRecoilDefaultValue(doorsTypeIds)) return;
      if (doorsTypeIds.length) {
        doorsTypeIds
          .map((doorsTypeId) => Number(doorsTypeId))
          .forEach((doorsTypeId) => {
            const doorsType = doorsTypesDraft.find(
              (doorsType) => doorsType.id === doorsTypeId,
            );

            if (!doorsType) return;
            doorsType.isSelected = true;
          });

        return;
      }
    });

    set(doorsFilterState, updatedDoorsType);
  },
});
